import { useEffect, useRef } from "react";

import { refreshServerAuthGateState } from "../../environments/primary/forgeSessionClient";
import { captureWebError } from "../../sentry";
import {
  resolveLoginAuthRecheckOutcome,
  shouldRecheckLoginAuthSession,
} from "./loginAuthRecheck.logic";

export function LoginAuthSessionRecheckCoordinator({
  onAuthenticated,
  onStillRequiresAuth,
}: {
  readonly onAuthenticated: () => void;
  readonly onStillRequiresAuth: (errorMessage: string | undefined) => void;
}) {
  const onAuthenticatedRef = useRef(onAuthenticated);
  const onStillRequiresAuthRef = useRef(onStillRequiresAuth);

  useEffect(() => {
    onAuthenticatedRef.current = onAuthenticated;
    onStillRequiresAuthRef.current = onStillRequiresAuth;
  }, [onAuthenticated, onStillRequiresAuth]);

  useEffect(() => {
    let recheckInFlight = false;

    const recheckAuthSession = async () => {
      if (recheckInFlight || !shouldRecheckLoginAuthSession(document.visibilityState)) {
        return;
      }

      recheckInFlight = true;
      try {
        const outcome = resolveLoginAuthRecheckOutcome(await refreshServerAuthGateState());
        if (outcome.kind === "authenticated") {
          onAuthenticatedRef.current();
          return;
        }
        onStillRequiresAuthRef.current(outcome.errorMessage);
      } catch (error) {
        // These rechecks are fire-and-forget background work, so a rejected
        // refresh (server restarting, transient retries exhausted) would
        // otherwise surface as an unhandled rejection. Capture it and leave
        // the login surface as it is — a network blip is not a reason to wipe
        // the form the user is filling in. The next focus/pageshow retries.
        captureWebError(error, { route: "login-auth-session-recheck" });
      } finally {
        recheckInFlight = false;
      }
    };

    const handleRecheck = () => {
      void recheckAuthSession();
    };

    void recheckAuthSession();

    window.addEventListener("focus", handleRecheck);
    document.addEventListener("visibilitychange", handleRecheck);
    window.addEventListener("pageshow", handleRecheck);

    return () => {
      window.removeEventListener("focus", handleRecheck);
      document.removeEventListener("visibilitychange", handleRecheck);
      window.removeEventListener("pageshow", handleRecheck);
    };
  }, []);

  return null;
}
